import React, { useState, useContext, useEffect } from 'react';
import {Form, Select, DatePicker, Checkbox, Switch, Radio, Input, Space, Divider} from 'antd';
import dayjs from 'dayjs';
import GenericDialog from "@common/GenericDialog";
import { BrandContext } from "@context/BrandContext";
import { UserContext } from "@context/UserContext";
import axios from "@common/axios";

const { TextArea } = Input;

// Helper to convert days to weeks if divisible by 7
const convertDaysToUnit = (days) => {
    if (days && days % 7 === 0) {
        return { value: days / 7, unit: 'weeks' };
    }
    return { value: days || 7, unit: 'days' };
};

const ScheduleReportDialog = ({ visible, handleClose, selectedClassId, onScheduleSuccess, initialDays, initialMetrics }) => {
    const brandConfig = useContext(BrandContext);
    const { user } = useContext(UserContext);
    const [form] = Form.useForm();

    // Compute initial range values from props
    const initialRange = convertDaysToUnit(initialDays);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedMetrics, setSelectedMetrics] = useState(initialMetrics?.length > 0 ? initialMetrics : ['all']);
    const [excludeInactiveUsers, setExcludeInactiveUsers] = useState(false);
    const [teacherClasses, setTeacherClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState(null);
    const [showAllClasses, setShowAllClasses] = useState(false);
    const [allSchoolClasses, setAllSchoolClasses] = useState([]);

    // Date range settings
    const [customRangeNumber, setCustomRangeNumber] = useState(initialRange.value);
    const [customRangeUnit, setCustomRangeUnit] = useState(initialRange.unit);

    // First run date (default to tomorrow)
    const [firstRunDate, setFirstRunDate] = useState(dayjs().add(1, 'day'));

    // Frequency settings
    const [customFrequencyNumber, setCustomFrequencyNumber] = useState(7);
    const [customFrequencyUnit, setCustomFrequencyUnit] = useState('days');

    const metricOptions = [
        { label: 'All', value: 'all' },
        { label: 'Sessions Started', value: 'sessionsStarted' },
        { label: 'Last Login', value: 'lastLogin' },
        { label: 'Exercises Complete', value: 'exercisesComplete' },
        { label: 'Walkthroughs Complete', value: 'walkthroughsComplete' },
        { label: 'Section Quizzes Complete', value: 'quizzesComplete' },
        { label: 'Questions Complete', value: 'quizQuestions' },
        { label: `${brandConfig?.testsName || 'Tests'} Complete`, value: 'testsComplete' },
        { label: 'Scorchers Complete', value: 'scorchersComplete'}
    ].filter(option => {
        if (brandConfig?.brandCode === 'DYNAMICENGLISH') {
            return option.value !== 'exercisesComplete' && option.value !== 'walkthroughsComplete';
        }
        return true;
    });

    const unitOptions = [
        { label: 'Days', value: 'days' },
        { label: 'Weeks', value: 'weeks' },
        { label: 'Months', value: 'months' },
    ];

    const handleMetricsChange = (newValues) => {
        // If "All" is being selected
        if (newValues.includes('all') && !selectedMetrics.includes('all')) {
            setSelectedMetrics(['all']);
        }
        // If other options are being selected while "All" is selected
        else if (selectedMetrics.includes('all') && newValues.length > 1) {
            setSelectedMetrics(newValues.filter(v => v !== 'all'));
        }
        // Normal selection
        else {
            setSelectedMetrics(newValues);
        }
    };

    useEffect(() => {
        if (visible && !selectedClassId) {
            loadClasses();
        }
    }, [visible, selectedClassId]);

    useEffect(() => {
        if (showAllClasses) {
            loadAllSchoolClasses();
        }
    }, [showAllClasses]);

    // Sync form fields with state
    useEffect(() => {
        form.setFieldsValue({
            name,
            class: selectedClass,
            firstRunDate,
            metrics: selectedMetrics
        });
    }, [name, selectedClass, firstRunDate, selectedMetrics, form]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!visible) {
            const resetRange = convertDaysToUnit(initialDays);
            setName('');
            setDescription('');
            setSelectedMetrics(initialMetrics?.length > 0 ? initialMetrics : ['all']);
            setExcludeInactiveUsers(false);
            setSelectedClass(null);
            setShowAllClasses(false);
            setCustomRangeNumber(resetRange.value);
            setCustomRangeUnit(resetRange.unit);
            setFirstRunDate(dayjs().add(1, 'day'));
            setCustomFrequencyNumber(7);
            setCustomFrequencyUnit('days');
            form.resetFields();
        }
    }, [visible, form, initialDays, initialMetrics]);

    const loadClasses = async () => {
        try {
            const response = await fetch('/controlCentre/getTeacherClasses.action');
            const data = await response.json();

            const classOptions = data.classes.map(cls => ({
                label: cls.className,
                value: cls.classId,
            }));

            setTeacherClasses(classOptions);
        } catch (error) {
            console.error('Failed to load teacher classes:', error);
        }
    };

    const loadAllSchoolClasses = async () => {
        try {
            const response = await fetch('/web/ui/sc/classes');
            const data = await response.json();

            const classOptions = Object.values(data).map(cls => ({
                label: cls.className,
                value: cls.classId || cls.id,
            }));

            setAllSchoolClasses(classOptions);
        } catch (error) {
            console.error('Failed to load all school classes:', error);
        }
    };

    const handleShowAllClassesChange = (checked) => {
        setShowAllClasses(checked);
        if (!checked) {
            setSelectedClass(null);
        }
    };

    const getAvailableClasses = () => {
        return showAllClasses ? allSchoolClasses : teacherClasses;
    };

    const renderClassSelectDropdown = (menu) => (
        <div>
            <div style={{
                padding: '8px 12px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }}>
                <Switch
                    checked={showAllClasses}
                    onChange={handleShowAllClassesChange}
                    size="small"
                />
                <span style={{ fontSize: '14px' }}>Show all classes</span>
            </div>
            {menu}
        </div>
    );

    const handleSchedule = async () => {
        // Validate form fields
        try {
            await form.validateFields();
        } catch (error) {
            // Form validation failed, don't proceed
            return;
        }

        const classId = selectedClassId || selectedClass;

        try {
            const payload = {
                reportName: name,
                description: description || null,
                classId: classId,
                dateRangeType: 'adaptive',
                adaptiveRangeValue: customRangeNumber,
                adaptiveRangeUnit: customRangeUnit.toUpperCase(),
                selectedMetrics: selectedMetrics.includes('all') ? [] : selectedMetrics,
                excludeInactiveStudents: excludeInactiveUsers,
                includeBelowAverage: true,
                firstRunDate: firstRunDate.toDate(),
                frequencyValue: customFrequencyNumber,
                frequencyUnit: customFrequencyUnit.toUpperCase(),
                recipientEmails: [user?.email],
                studentComments: [],
                classAverages: {},
                studentCount: 0
            };

            await axios.post('/web/ui/report/teacher/activity/scheduleActivitySummary', payload);
            displayToastMessage('Schedule created successfully', null, 'success', null, 10000);
            if (onScheduleSuccess) {
                onScheduleSuccess();
            }
            handleClose();
        } catch (error) {
            console.error('Error scheduling activity summary:', error);
            displayToastMessage(error.response?.data?.error || 'Failed to create schedule', null, 'error', null, 10000);
        }
    };

    return (
        <GenericDialog
            title="Schedule Summary Email"
            open={visible}
            dialogWidth={450}
            onClose={handleClose}
            buttons={[
                {
                    name: 'Cancel',
                    extraProps: {type: 'text'}
                },
                {
                    name: 'Schedule',
                    callback: handleSchedule,
                    extraProps: {
                        type: 'primary'
                    }
                }
            ]}
        >
            <p>Schedule an activity summary to be automatically created and sent to your email at a regular interval.</p>
            <Divider className={'ch-divider'} />
            <Form
                form={form}
                layout="vertical"
                className={'ch-form'}
            >
                <Form.Item
                    label="Name"
                    name="name"
                    rules={[
                        { required: true, message: 'Please enter a name for this schedule' },
                        { whitespace: true, message: 'Schedule name cannot be empty' }
                    ]}
                >
                    <Input
                        placeholder="Enter schedule name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </Form.Item>

                <Form.Item label="Description">
                    <TextArea
                        placeholder="Enter schedule description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={2}
                    />
                </Form.Item>
                {!selectedClassId && (
                    <Form.Item
                        label="Class"
                        name="class"
                        rules={[
                            { required: true, message: 'Please select a class' }
                        ]}
                    >
                        <Select
                            placeholder="Select class"
                            options={getAvailableClasses()}
                            value={selectedClass}
                            onChange={setSelectedClass}
                            showSearch
                            filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                            }
                            popupRender={renderClassSelectDropdown}
                        />
                    </Form.Item>
                )}
                <Form.Item
                    label="Metrics"
                    name="metrics"
                    rules={[
                        { required: true, message: 'Please select at least one metric' }
                    ]}
                >
                    <Select
                        mode="multiple"
                        placeholder="Select metrics"
                        options={metricOptions}
                        value={selectedMetrics}
                        onChange={handleMetricsChange}
                        allowClear={true}
                    />
                </Form.Item>
                <Form.Item label="The summary should include">
                    <Space.Compact style={{width: '100%'}}>
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '0 11px',
                            border: '1px solid #d9d9d9',
                            borderRight: 'none',
                            borderRadius: '6px 0 0 6px',
                            background: '#fafafa',
                        }}>
                            Previous
                        </span>
                        <Input
                            type="number"
                            min={1}
                            value={customRangeNumber}
                            onChange={(e) => setCustomRangeNumber(parseInt(e.target.value) || 1)}
                            style={{borderRadius: 0}}
                        />
                        <Select
                            options={unitOptions}
                            value={customRangeUnit}
                            onChange={setCustomRangeUnit}
                        />
                    </Space.Compact>
                </Form.Item>
                <Form.Item
                    label="First run this summary"
                    name="firstRunDate"
                    rules={[
                        { required: true, message: 'Please select a first run date' }
                    ]}
                >
                    <DatePicker
                        value={firstRunDate}
                        onChange={setFirstRunDate}
                        format={user?.getDateFormatString?.() || 'DD/MM/YYYY'}
                        style={{width: '100%'}}
                        disabledDate={(current) => current && current < dayjs().add(1, 'day').startOf('day')}
                    />
                </Form.Item>
                <Form.Item label="Run this summary">
                    <Space.Compact style={{width: '100%'}}>
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '0 11px',
                            border: '1px solid #d9d9d9',
                            borderRight: 'none',
                            borderRadius: '6px 0 0 6px',
                            background: '#fafafa',
                        }}>
                            Every
                        </span>
                        <Input
                            type="number"
                            min={1}
                            value={customFrequencyNumber}
                            onChange={(e) => setCustomFrequencyNumber(parseInt(e.target.value) || 1)}
                            style={{borderRadius: 0}}
                        />
                        <Select
                            options={unitOptions}
                            value={customFrequencyUnit}
                            onChange={setCustomFrequencyUnit}
                        />
                    </Space.Compact>
                </Form.Item>
                <Form.Item>
                    <Checkbox
                        checked={excludeInactiveUsers}
                        onChange={(e) => setExcludeInactiveUsers(e.target.checked)}
                    >
                        Exclude students who have not logged in
                    </Checkbox>
                </Form.Item>
            </Form>
        </GenericDialog>
    );
};

export default ScheduleReportDialog;
